import React, { useState, useCallback, useEffect } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { formatUtcDate, formatUtcDateTime } from "../utils/dateTime";

// The Enterprise "Deleted assessments" audit list: which assessment was deleted, by whom and when.
// A deleted assessment's card (and so its Activity dropdown) is gone, so this is where its deletion
// shows. level: "org" | "deal" | "businessUnit" — the page's own scope; the server limits the rows to
// the deals / business units the user can browse. Loaded when opened, and again after each delete
// (refreshKey) while open. Super admin only, like deleting itself; nobody else sees the panel.
const EnterpriseDeletedAssessments = ({ level, refreshKey = 0 }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const apiCall = useApi();
  const { canDeleteAssessments } = useRBAC();

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await apiCall("GET", `/enterpriseAssessments/deleted?level=${level}`);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [apiCall, level]);

  useEffect(() => {
    if (open) load();
  }, [open, refreshKey, load]);

  if (!canDeleteAssessments()) return null;

  return (
    <div className="mt-4 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="enterprise-deleted-assessments">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Trash2 className="h-4 w-4 text-gray-400" aria-hidden="true" />
          Deleted assessments
          {rows !== null && (
            <span className="px-1.5 py-0.5 bg-gray-100 rounded-full text-xs font-medium text-gray-600">{rows.length}</span>
          )}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-2">
          {loading && rows === null && <p className="text-xs text-gray-400 py-2">Loading…</p>}
          {failed && <p className="text-xs text-red-600 py-2">Could not load deleted assessments.</p>}
          {!loading && rows?.length === 0 && <p className="text-xs text-gray-400 py-2">No assessments have been deleted.</p>}
          {rows?.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {rows.map((r) => (
                <li key={r.assessmentId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 text-xs"
                    data-testid={`deleted-assessment-${r.assessmentId}`}>
                  <span className="text-gray-700">
                    {r.scopeName && <span className="font-medium">{r.scopeName} · </span>}
                    Assessment of {formatUtcDate(r.assessmentDate)}
                  </span>
                  <span className="text-gray-500">
                    {r.deletedByUserName
                      ? <>Deleted by <span className="font-medium text-gray-700">{r.deletedByUserName}</span></>
                      : <span className="italic">Deleted by: not recorded</span>}
                    {r.deletedAt && <> on {formatUtcDateTime(r.deletedAt)}</>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default EnterpriseDeletedAssessments;
